import { useState } from "react";
import { StatusBadge, EmptyState } from "@/components/shared";
import { Wrench, Send, Mail, CheckCircle2, Plus, Trash2, HardHat, Sparkles, Loader2, Star, RefreshCw } from "lucide-react";
import api, { formatDate, formatDateTime } from "@/lib/api";
import { toast } from "sonner";

const StarRating = ({ value = 0, onChange, size = "h-4 w-4", readOnly = false }) => {
  const [hover, setHover] = useState(0);
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={readOnly}
          data-testid={readOnly ? undefined : `star-${n}`}
          onMouseEnter={() => !readOnly && setHover(n)}
          onMouseLeave={() => !readOnly && setHover(0)}
          onClick={() => !readOnly && onChange?.(n)}
          className={readOnly ? "cursor-default" : "cursor-pointer"}
        >
          <Star className={`${size} ${(hover || value) >= n ? "fill-amber-400 text-amber-400" : "text-line"}`} />
        </button>
      ))}
    </span>
  );
};

const RatingDisplay = ({ avg, count }) => {
  if (!count) return <span className="text-[11px] text-mute">Not rated yet</span>;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-mute" title={`${avg} average from ${count} job${count > 1 ? "s" : ""}`}>
      <StarRating value={Math.round(avg)} readOnly size="h-3 w-3" />
      <span className="font-medium text-ink">{avg}</span>
      <span>({count})</span>
    </span>
  );
};

const ContractorManager = ({ contractors, addContractor, deleteContractor }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", trade: "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) return;
    setSaving(true);
    const ok = await addContractor({ name: form.name.trim(), email: form.email.trim(), trade: form.trade.trim() });
    setSaving(false);
    if (ok) setForm({ name: "", email: "", trade: "" });
  };

  return (
    <div className="bg-white border border-line rounded-xl p-5 mb-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <HardHat className="h-5 w-5 text-forest" />
          <h3 className="font-heading font-medium text-ink">Your contractors</h3>
          <span className="text-xs text-mute">({contractors.length})</span>
        </div>
        <button data-testid="contractors-toggle" onClick={() => setOpen(!open)} className="text-sm text-forest hover:underline">{open ? "Hide" : "Manage"}</button>
      </div>
      <p className="text-sm text-mute mt-1">Save contractors & employees once, then dispatch maintenance requests to them in one tap.</p>

      {open && (
        <div className="mt-4 space-y-3">
          {contractors.length > 0 && (
            <div className="divide-y divide-line border border-line rounded-lg overflow-hidden">
              {contractors.map((c) => (
                <div key={c.id} data-testid={`contractor-row-${c.id}`} className="flex items-center justify-between gap-3 px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink truncate">{c.name} {c.trade && <span className="text-xs font-normal text-forest">· {c.trade}</span>}</p>
                    <p className="text-xs text-mute truncate">{c.email}</p>
                    <div className="mt-0.5"><RatingDisplay avg={c.avg_rating} count={c.rating_count} /></div>
                  </div>
                  <button data-testid={`contractor-delete-${c.id}`} onClick={() => deleteContractor(c.id)} className="text-mute hover:text-red-600 shrink-0" title="Remove"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
          <div className="grid sm:grid-cols-3 gap-2">
            <input data-testid="contractor-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="border border-line rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest/20" />
            <input data-testid="contractor-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="border border-line rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest/20" />
            <input data-testid="contractor-trade-input" value={form.trade} onChange={(e) => setForm({ ...form, trade: e.target.value })} placeholder="Trade (e.g. Plumber)" className="border border-line rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest/20" />
          </div>
          <button data-testid="contractor-save-btn" disabled={saving || !form.name.trim() || !form.email.trim()} onClick={save} className="inline-flex items-center gap-1.5 bg-forest text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-forest-hover disabled:opacity-60">
            <Plus className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Add contractor"}
          </button>
        </div>
      )}
    </div>
  );
};

const DispatchEntry = ({ m, dd, resendDispatch, rateContractor }) => {
  const [busy, setBusy] = useState(false);
  const [showRate, setShowRate] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const dstatus = dd.status || "dispatched";
  const badge = { dispatched: "bg-amber-100 text-amber-800", accepted: "bg-blue-100 text-blue-800", completed: "bg-emerald-100 text-emerald-800" }[dstatus];
  const dot = { dispatched: "bg-amber-500", accepted: "bg-blue-500", completed: "bg-emerald-500" }[dstatus];
  const blabel = { dispatched: "Dispatched", accepted: "Accepted", completed: "Completed" }[dstatus];

  const resend = async () => { setBusy(true); await resendDispatch(m.id, dd.token); setBusy(false); };
  const submitRating = async () => {
    if (!rating) return;
    setBusy(true);
    const ok = await rateContractor(m.id, dd.token, rating, comment.trim());
    setBusy(false);
    if (ok) setShowRate(false);
  };

  return (
    <li data-testid={`dispatch-entry-${m.id}`} className="ml-3.5">
      <span className={`absolute -left-[5px] mt-1 h-2 w-2 rounded-full ${dot}`} />
      <p className="text-xs text-ink flex items-center gap-1.5 flex-wrap">
        <span className="font-medium">{dd.name || dd.email}</span>
        {dd.trade ? <span className="text-forest">· {dd.trade}</span> : null}
        <span data-testid={`dispatch-status-${m.id}`} className={`text-[10px] font-medium rounded-full px-1.5 py-0.5 ${badge}`}>{blabel}</span>
      </p>
      <p className="text-[11px] text-mute">{dd.email} · {formatDateTime(dd.at) || formatDate(dd.at)}</p>
      {dd.note && <p className="text-[11px] text-mute italic mt-0.5">"{dd.note}"</p>}

      <div className="flex items-center gap-3 mt-1.5">
        {dstatus !== "completed" && (
          <button data-testid={`dispatch-resend-${dd.token}`} onClick={resend} disabled={busy} className="inline-flex items-center gap-1 text-[11px] text-forest hover:underline disabled:opacity-50">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Resend link
          </button>
        )}
        {dstatus === "completed" && !dd.rated && !showRate && (
          <button data-testid={`dispatch-rate-toggle-${dd.token}`} onClick={() => setShowRate(true)} className="inline-flex items-center gap-1 text-[11px] text-forest hover:underline">
            <Star className="h-3 w-3" /> Rate this contractor
          </button>
        )}
        {dstatus === "completed" && dd.rated && (
          <span data-testid={`dispatch-rated-${dd.token}`} className="inline-flex items-center gap-1 text-[11px] text-mute"><StarRating value={dd.rating || 0} readOnly size="h-3 w-3" /> Rated</span>
        )}
      </div>

      {showRate && (
        <div data-testid={`dispatch-rate-form-${dd.token}`} className="mt-2 rounded-lg border border-line bg-bone/40 p-2.5 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-ink font-medium">How did they do?</span>
            <StarRating value={rating} onChange={setRating} />
          </div>
          <input data-testid={`dispatch-rate-comment-${dd.token}`} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Optional note (e.g. fast, tidy, fair price)" className="w-full border border-line rounded px-2 py-1 text-xs" />
          <div className="flex gap-2">
            <button data-testid={`dispatch-rate-save-${dd.token}`} onClick={submitRating} disabled={busy || !rating} className="bg-forest text-white rounded px-3 py-1 text-xs font-medium hover:bg-forest-hover disabled:opacity-50">{busy ? "Saving…" : "Save rating"}</button>
            <button onClick={() => setShowRate(false)} className="border border-line rounded px-3 py-1 text-xs hover:bg-white">Cancel</button>
          </div>
        </div>
      )}
    </li>
  );
};

const MaintCard = ({ m, updateMaint, dispatchMaint, resendDispatch, rateContractor, contractors, isPro }) => {
  const [email, setEmail] = useState(m.dispatched_to || "");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(false);
  const [triage, setTriage] = useState(null);
  const [triaging, setTriaging] = useState(false);

  const send = async () => {
    if (!email.trim()) return;
    setSending(true);
    const ok = await dispatchMaint(m.id, email.trim(), note.trim());
    setSending(false);
    if (ok) { setNote(""); setOpen(false); }
  };

  const runTriage = async () => {
    setTriaging(true);
    try {
      const { data } = await api.post("/ai/maintenance", { title: m.title, description: m.description || "" });
      setTriage(data);
    } catch (e) { toast.error(e.response?.data?.detail || "AI triage failed"); }
    finally { setTriaging(false); }
  };

  return (
    <div className="bg-white border border-line rounded-xl p-5">
      <div className="flex items-center gap-2"><StatusBadge status={m.priority} /><StatusBadge status={m.status} /></div>
      <h3 className="mt-2 font-medium text-ink">{m.title}</h3>
      <p className="text-sm text-mute">{m.property_title} · {m.renter_name}</p>
      <p className="text-sm text-mute mt-2">{m.description}</p>
      {m.photo && <img src={m.photo} alt="" className="mt-3 rounded-lg max-h-40 object-cover" />}

      {isPro && (
        <div className="mt-3">
          {!triage ? (
            <button data-testid={`maint-ai-triage-${m.id}`} disabled={triaging} onClick={runTriage} className="inline-flex items-center gap-1.5 text-sm text-forest border border-forest/30 rounded-lg px-3 py-1.5 hover:bg-forest/5 disabled:opacity-60">
              {triaging ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} AI triage
            </button>
          ) : (
            <div data-testid={`maint-ai-result-${m.id}`} className="rounded-lg border border-forest/20 bg-forest/[0.04] p-3 text-sm">
              <div className="flex flex-wrap gap-2 mb-1.5">
                <span className="text-xs font-semibold rounded-full bg-white border border-line px-2 py-0.5">{triage.category}</span>
                <span className="text-xs font-semibold rounded-full bg-white border border-line px-2 py-0.5 capitalize">Priority: {triage.priority}</span>
                {triage.contractor_category && <span className="text-xs font-semibold rounded-full bg-white border border-line px-2 py-0.5">Dispatch: {triage.contractor_category}</span>}
              </div>
              <p className="text-ink">{triage.summary}</p>
              {m.status !== "resolved" && <button data-testid={`maint-ai-usenote-${m.id}`} onClick={() => { setNote(triage.summary); setOpen(true); }} className="mt-1.5 text-xs font-medium text-forest hover:underline">Use as dispatch note</button>}
            </div>
          )}
        </div>
      )}

      {m.status !== "resolved" && (
        <div className="flex gap-2 mt-3">
          {m.status === "open" && <button data-testid={`maint-progress-${m.id}`} onClick={() => updateMaint(m.id, "in_progress")} className="border border-line px-3 py-1.5 rounded-lg text-sm hover:bg-bone">Mark In Progress</button>}
          <button data-testid={`maint-resolve-${m.id}`} onClick={() => updateMaint(m.id, "resolved")} className="bg-forest text-white px-3 py-1.5 rounded-lg text-sm hover:bg-forest-hover">Resolve</button>
        </div>
      )}

      {/* Dispatch to a contractor / employee */}
      <div className="mt-4 pt-4 border-t border-line">
        {(m.dispatch_history || []).length > 0 && (
          <div data-testid={`maint-dispatch-log-${m.id}`} className="mb-3">
            <p className="text-xs font-semibold text-ink flex items-center gap-1.5 mb-2"><HardHat className="h-3.5 w-3.5 text-forest" /> Dispatch log</p>
            <ol className="relative border-l border-line ml-1.5 space-y-3">
              {[...m.dispatch_history].reverse().map((dd) => (
                <DispatchEntry key={dd.id || dd.token || dd.at} m={m} dd={dd} resendDispatch={resendDispatch} rateContractor={rateContractor} />
              ))}
            </ol>
          </div>
        )}
        {m.dispatched_to && !(m.dispatch_history || []).length && (
          <p data-testid={`maint-dispatched-${m.id}`} className="text-xs text-forest flex items-center gap-1.5 mb-2">
            <CheckCircle2 className="h-3.5 w-3.5" /> Sent to {m.dispatched_to}{m.dispatched_at ? ` on ${formatDate(m.dispatched_at)}` : ""}
          </p>
        )}
        {!open ? (
          <button data-testid={`maint-assign-toggle-${m.id}`} onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-sm text-forest border border-forest/30 rounded-lg px-3 py-1.5 hover:bg-forest/5 transition-colors">
            <Mail className="h-3.5 w-3.5" /> {m.dispatched_to ? "Send again / to another" : "Assign to contractor"}
          </button>
        ) : (
          <div className="space-y-2">
            {contractors.length > 0 && (
              <div>
                <label className="text-xs font-medium text-ink">Pick a saved contractor</label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {contractors.map((c) => (
                    <button
                      key={c.id}
                      data-testid={`maint-pick-contractor-${m.id}-${c.id}`}
                      onClick={() => setEmail(c.email)}
                      className={`text-xs rounded-full px-2.5 py-1 border transition-colors ${email === c.email ? "border-forest bg-forest/10 text-forest" : "border-line text-ink hover:bg-bone"}`}
                    >
                      {c.name}{c.trade ? ` · ${c.trade}` : ""}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <label className="text-xs font-medium text-ink">Contractor / employee email</label>
            <input
              data-testid={`maint-contractor-email-${m.id}`}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="contractor@example.com"
              className="w-full border border-line rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest/20"
            />
            <label className="text-xs font-medium text-ink">Note / instructions <span className="text-mute font-normal">(optional)</span></label>
            <textarea
              data-testid={`maint-note-${m.id}`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="e.g. Tenant is home after 5pm; please call first."
              className="w-full border border-line rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest/20"
            />
            <p className="text-[11px] text-mute">We'll include the tenant's name, email, phone, the property address and the issue details.</p>
            <div className="flex gap-2">
              <button data-testid={`maint-send-${m.id}`} disabled={sending || !email.trim()} onClick={send} className="inline-flex items-center gap-1.5 bg-forest text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-forest-hover disabled:opacity-60">
                <Send className="h-3.5 w-3.5" /> {sending ? "Sending…" : "Send request"}
              </button>
              <button data-testid={`maint-cancel-${m.id}`} onClick={() => setOpen(false)} className="border border-line px-3 py-2 rounded-lg text-sm hover:bg-bone">Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const MaintenanceTab = ({ maintenance, updateMaint, dispatchMaint, resendDispatch, rateContractor, contractors = [], addContractor, deleteContractor, isPro = false }) => {
  return (
    <div>
      <ContractorManager contractors={contractors} addContractor={addContractor} deleteContractor={deleteContractor} />
      {maintenance.length === 0 ? (
        <div className="bg-white border border-line rounded-xl"><EmptyState icon={Wrench} title="No maintenance requests" /></div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {maintenance.map((m) => (
            <MaintCard key={m.id} m={m} updateMaint={updateMaint} dispatchMaint={dispatchMaint} resendDispatch={resendDispatch} rateContractor={rateContractor} contractors={contractors} isPro={isPro} />
          ))}
        </div>
      )}
    </div>
  );
};
